// Authentication and session management for Docospot
// Handles JWT tokens, auto-refresh, and inactivity timeouts

import { API_BASE_URL } from './config.js';

/**
 * AuthManager - Manages JWT authentication tokens
 * Handles token storage, validation, refresh, and expiry checking
 */
export class AuthManager {
    constructor() {
        this.token = localStorage.getItem('access_token');
        this.refreshTimer = null;
        this.isRefreshing = false;
        this.isLoggingOut = false;
        this.initializeTokenRefresh();
    }

    setToken(token) {
        this.token = token;
        localStorage.setItem('access_token', token);
        this.isLoggingOut = false; // Reset logout flag when setting new token
        this.initializeTokenRefresh();
    }

    getToken() {
        return this.token;
    }

    removeToken() {
        this.isLoggingOut = true;
        this.token = null;
        localStorage.removeItem('access_token');
        this.clearRefreshTimer();
    }

    isAuthenticated() {
        // Check if token exists and is not empty
        if (!this.token || this.token.trim() === '') {
            return false;
        }
        
        // Basic JWT format check (should have 3 parts separated by dots)
        const tokenParts = this.token.split('.');
        if (tokenParts.length !== 3) {
            console.warn('Invalid token format detected, clearing token');
            this.removeToken();
            return false;
        }
        
        // Check if token is expired
        if (this.isTokenExpired()) {
            console.warn('Token is expired');
            return false;
        }
        
        return true;
    }

    isTokenExpired() {
        if (!this.token) return true;
        
        try {
            const tokenParts = this.token.split('.');
            if (tokenParts.length !== 3) return true;
            
            const payload = JSON.parse(atob(tokenParts[1]));
            const exp = payload.exp;
            
            if (!exp) return true;
            
            // Check if token expires in the next 5 minutes (300 seconds)
            const now = Math.floor(Date.now() / 1000);
            return exp <= (now + 300); // Refresh 5 minutes before expiry
        } catch (e) {
            console.error('Error parsing token:', e);
            return true;
        }
    }

    shouldRefreshForActivity() {
        if (!this.token) return false;
        
        try {
            const tokenParts = this.token.split('.');
            if (tokenParts.length !== 3) return false;
            
            const payload = JSON.parse(atob(tokenParts[1]));
            const exp = payload.exp;
            
            if (!exp) return false;
            
            // Check if token expires in the next 10 minutes (600 seconds)
            // This gives us a wider window for activity-based refresh
            const now = Math.floor(Date.now() / 1000);
            return exp <= (now + 600); // Refresh 10 minutes before expiry when user is active
        } catch (e) {
            console.error('Error parsing token:', e);
            return false;
        }
    }

    getTokenExpiryTime() {
        if (!this.token) return null;
        
        try {
            const tokenParts = this.token.split('.');
            if (tokenParts.length !== 3) return null;
            
            const payload = JSON.parse(atob(tokenParts[1]));
            return payload.exp ? new Date(payload.exp * 1000) : null;
        } catch (e) {
            console.error('Error parsing token expiry:', e);
            return null;
        }
    }

    initializeTokenRefresh() {
        this.clearRefreshTimer();
        
        if (!this.token || this.isLoggingOut) return;
        
        const expiryTime = this.getTokenExpiryTime();
        if (!expiryTime) return;
        
        // Schedule refresh 5 minutes before expiry
        const refreshTime = expiryTime.getTime() - Date.now() - (5 * 60 * 1000);
        
        if (refreshTime > 0) {
            console.log(`📅 Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);
            this.refreshTimer = setTimeout(() => {
                this.refreshToken();
            }, refreshTime);
        } else {
            // Token expires soon or is already expired, try to refresh immediately
            console.log('🔄 Token expires soon, attempting immediate refresh...');
            this.refreshToken();
        }
    }

    clearRefreshTimer() {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    async refreshToken() {
        // Prevent refresh during logout or multiple simultaneous refresh attempts
        if (this.isLoggingOut) {
            console.log('🚪 Logout in progress, skipping token refresh');
            return false;
        }
        if (this.isRefreshing) {
            console.log('🔄 Token refresh already in progress, skipping...');
            return false;
        }
        
        try {
            this.isRefreshing = true;
            console.log('🔄 Attempting to refresh token...');
            
            const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                headers: this.getAuthHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                this.setToken(data.access_token);
                console.log('✅ Token refreshed successfully');
                
                // Reset the inactivity timer since user session is still active
                if (window.inactivityManager) {
                    window.inactivityManager.resetTimer();
                }
                
                return true;
            } else {
                console.error('❌ Token refresh failed:', response.status, response.statusText);
                
                // Try to get the error message from the response
                let errorMessage = 'Your session has expired. Please log in again.';
                try {
                    const errorData = await response.json();
                    if (errorData.detail) {
                        errorMessage = errorData.detail;
                    }
                } catch (e) {
                    // If we can't parse the error, use the default message
                }
                
                this.handleRefreshFailure(errorMessage);
                return false;
            }
        } catch (error) {
            console.error('❌ Token refresh error:', error);
            this.handleRefreshFailure();
            return false;
        } finally {
            this.isRefreshing = false;
        }
    }

    handleRefreshFailure(message = 'Your session has expired. Please log in again.') {
        console.log('🚪 Token refresh failed, logging out user');
        this.removeToken();
        
        // Call global autoLogout if available
        if (window.autoLogout) {
            window.autoLogout(message);
        }
    }

    getAuthHeaders() {
        return {
            'Authorization': `Bearer ${this.token}`,
            'Content-Type': 'application/json'
        };
    }
}

/**
 * InactivityManager - Monitors user activity and auto-logout after inactivity
 * Tracks mouse, keyboard, touch, and scroll events
 */
export class InactivityManager {
    constructor() {
        // 2 hours of inactivity before auto-logout (tokens refresh automatically every 25 minutes)
        this.timeout = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        this.timeoutId = null;
        this.isActive = false;
        this.activityThrottle = false;
        
        // Events that count as user activity
        this.activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    }
    
    start() {
        if (this.isActive) return; // Already started

        this.isActive = true;
        console.log('⏱️ Inactivity timer started (2 hour timeout)');

        // Store bound reference so the same function can be removed later
        this._boundHandleActivity = this.handleActivity.bind(this);

        // Set initial timeout
        this.resetTimer();

        // Add event listeners for user activity
        this.activityEvents.forEach(event => {
            document.addEventListener(event, this._boundHandleActivity, true);
        });
    }

    stop() {
        if (!this.isActive) return; // Already stopped

        this.isActive = false;
        console.log('⏱️ Inactivity timer stopped');

        // Clear timeout
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        // Remove event listeners using the stored bound reference
        if (this._boundHandleActivity) {
            this.activityEvents.forEach(event => {
                document.removeEventListener(event, this._boundHandleActivity, true);
            });
            this._boundHandleActivity = null;
        }
    }
    
    handleActivity() {
        if (!this.isActive) return;
        
        // Throttle activity detection to avoid excessive timer resets
        if (this.activityThrottle) return;
        
        this.activityThrottle = true;
        setTimeout(() => {
            this.activityThrottle = false;
        }, 1000); // Throttle for 1 second
        
        // Check if token needs refresh (if expiring within 10 minutes)
        if (window.auth && window.auth.shouldRefreshForActivity()) {
            console.log('🔄 User is active and token is expiring soon, refreshing...');
            window.auth.refreshToken();
        }
        
        // Reset the timer on any user activity
        this.resetTimer();
    }
    
    resetTimer() {
        if (!this.isActive) return;
        
        // Clear existing timeout
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        
        // Set new timeout
        this.timeoutId = setTimeout(() => {
            console.log('⏱️ Inactivity timeout reached - logging out');
            
            // Call global autoLogout if available
            if (window.autoLogout) {
                window.autoLogout('You have been logged out due to extended inactivity.');
            }
        }, this.timeout);
    }
}

// Create and export singleton instances
export const auth = new AuthManager();
export const inactivityManager = new InactivityManager();

export default {
    AuthManager,
    InactivityManager,
    auth,
    inactivityManager
};
